import React from 'react';

/**
 * BrandLogo — single source of truth for the TURN-CONNECT wordmark.
 *
 * Why this exists:
 *   1. The logo asset shipped in 11 places with identical inline-img
 *      code; centralizing means swaps and rebrands touch one file.
 *   2. None of the inline copies set HTML width/height attrs, so the
 *      browser couldn't reserve space before the image loaded — small
 *      CLS risk and a Lighthouse "image elements do not have explicit
 *      width and height" warning.
 *   3. <picture> with a WebP source + PNG fallback so modern browsers
 *      get the smaller WebP (~30KB) while older Safari/etc. fall back
 *      to the original PNG (~38KB).
 *
 * Both source files are the same intrinsic dimensions (2123×335) — the
 * original logo.png asset, just re-encoded as WebP. CSS handles display
 * sizing; passing `height` only affects the rendered size.
 */
interface BrandLogoProps {
  /** CSS-rendered height in pixels (defaults to 40). */
  height?: number;
  /** Extra style overrides. */
  style?: React.CSSProperties;
  className?: string;
  /** Alt text — defaults to the brand name. */
  alt?: string;
  /**
   * Follow the app theme: white wordmark in dark, ink wordmark in light. Only
   * for surfaces painted from theme tokens (the app header). Pages that paint a
   * fixed black ground (login, public site) keep the default white wordmark,
   * which would otherwise turn ink-on-black there.
   */
  themeAware?: boolean;
}

// Intrinsic dimensions of apps/web/public/assets/logo.{png,webp}.
const INTRINSIC_W = 2123;
const INTRINSIC_H = 335;

// Vite substitutes process.env.PUBLIC_URL at build time with the configured
// base path (e.g. "/web" in prod). We MUST reference it directly — wrapping
// it in a `typeof process !== 'undefined' && process.env` guard the way the
// previous version did defeats the substitution because Vite only inlines
// exact `process.env.X` accesses, not runtime typeof checks. At runtime in
// the browser, `process` IS undefined, so the guard short-circuits and
// `base` resolved to "" — causing every logo to load from "/assets/logo.png"
// instead of "/web/assets/logo.png" → 404 → invisible logo. (User-reported
// regression that turned out to be a base-path bug, not a layout issue.)
const PUBLIC_URL: string = (process.env.PUBLIC_URL as string | undefined) || '';

export const BrandLogo: React.FC<BrandLogoProps> = ({ height = 40, style, className, alt = 'TURN-CONNECT', themeAware = false }) => {
  if (themeAware) {
    // Both variants render; App.css shows the one matching <html data-theme>.
    // The hidden one is display:none, so screen readers announce the logo once.
    return (
      <>
        <LogoPicture file="logo" height={height} style={style} className={joinClass(className, 'tc-logo-on-dark')} alt={alt} />
        <LogoPicture file="logo-ink" height={height} style={style} className={joinClass(className, 'tc-logo-on-light')} alt={alt} />
      </>
    );
  }
  return <LogoPicture file="logo" height={height} style={style} className={className} alt={alt} />;
};

const joinClass = (a: string | undefined, b: string): string => (a ? `${a} ${b}` : b);

// logo-ink.{png,webp} is logo.* with the white wordmark recoloured to the light
// theme's ink (#18181B); the scarlet mark is unchanged. Same intrinsic size.
const LogoPicture: React.FC<{
  file: 'logo' | 'logo-ink';
  height: number;
  style?: React.CSSProperties;
  className?: string;
  alt: string;
}> = ({ file, height, style, className, alt }) => {
  // display: contents on <picture> keeps the element transparent in the
  // layout tree so the <img> inside becomes the direct flex/grid item of
  // BrandLogo's parent. Defensive against parent containers that size
  // children via `> img` selectors or that constrain inline-element width.
  return (
    <picture style={{ display: 'contents' }}>
      <source srcSet={`${PUBLIC_URL}/assets/${file}.webp`} type="image/webp" />
      <img
        src={`${PUBLIC_URL}/assets/${file}.png`}
        alt={alt}
        width={INTRINSIC_W}
        height={INTRINSIC_H}
        className={className}
        style={{ height: `${height}px`, width: 'auto', ...style }}
      />
    </picture>
  );
};
